// © Microsoft Corporation. All rights reserved.

import { ContentActionsToolbarConfig, ContentActionsToolbarProps, ContentActionsToolbarState, Status } from ".";
import { IRequestContext, InspectorArgs, InspectorResponse } from "@msnews/core";
import { OptionItemDislike, OptionItemLike, OptionItemUndoDislike, OptionItemUndoLike } from "./components/sharing-toolbar/SharingToolbar.constants";

import { ButtonType } from "./components/sharing-toolbar/SharingToolbar";
import { CardContextMenuItem } from "@msnews/card-context-menu";
import { SocialMediaTarget } from "@msnews/views-helpers";
import { cloneDeep } from "lodash-es";

/**
 * Type for ContentActionsToolbar connectorArgs
 *
 * @export
 * @type {ContentActionsToolbarConnectorArgs}
 */
export type ContentActionsToolbarConnectorArgs = {
    /** Flag to indicate if there's a need to wait for content status endpoint response */
    isContentStatusRequired: boolean;
    /** The request context */
    requestContext: IRequestContext;
};

export type ContentActionsToolbarInspectorArgs = InspectorArgs<ContentActionsToolbarConfig, ContentActionsToolbarState, ContentActionsToolbarConnectorArgs>;

export namespace ContentActionsToolbarInspector {
    export function inspect(args: ContentActionsToolbarInspectorArgs): InspectorResponse<ContentActionsToolbarProps> {
        const { config, state, config: { localizedStrings } } = args;
        const { isContentStatusRequired, requestContext } = args.connectorArgs as ContentActionsToolbarConnectorArgs;

        const newProps: ContentActionsToolbarProps = {
            staticPosition: state.staticPosition,
            position: state.position,
            sharingToolbar: {
                position: state.position,
                sharingButtons: [],
                requestContext
            },
            contentStatus: args.state.status,
            sharingError: args.state.sharingError
        };

        const sharingShortUrl = state.documentData ? state.documentData.sharingShortUrl : undefined;
        const articleTitle = state.documentData ? state.documentData.articleTitle : undefined;
        const articleImageUrl = state.documentData ? state.documentData.articleImageUrl : undefined;
        const providerName = state.documentData ? state.documentData.providerName : undefined;
        const providerLogoUrl = state.documentData ? state.documentData.providerLogoUrl : undefined;

        if ((!isContentStatusRequired || state.status) && state.documentData) {
            newProps.sharingToolbar.sharingButtons = config.sharingButtons.map(buttonItem => {
                let newButtonItem = cloneDeep(buttonItem);
                switch (buttonItem.buttonType) {
                    case ButtonType.SocialMedia:
                        if (newButtonItem.buttonData) {
                            newButtonItem.buttonData.contentUrl = sharingShortUrl;
                            newButtonItem.buttonData.contentTitle = articleTitle;
                            newButtonItem.buttonData.emailBody = localizedStrings.emailBodyText;
                            newButtonItem.buttonData.target = buttonItem.buttonData.target ? SocialMediaTarget[buttonItem.buttonData.target] : undefined;
                        } else {
                            newButtonItem = undefined;
                        }
                        break;
                    case ButtonType.ShareDialog:
                        if (newButtonItem.buttonData && newButtonItem.buttonData.shareDialogProps && newButtonItem.buttonData.shareDialogProps.socialMediaItems) {
                            const { shareDialogProps } = newButtonItem.buttonData;
                            shareDialogProps.enabled = true;
                            shareDialogProps.contentUrl = sharingShortUrl;
                            shareDialogProps.contentTitle = articleTitle;
                            shareDialogProps.emailBody = localizedStrings.emailBodyText;
                            shareDialogProps.dialogStrings = {
                                dialogTitle: localizedStrings.shareDialogTitleText,
                                copyButton: localizedStrings.shareDialogCopyButtonText,
                                copyButtonAriaLabel: localizedStrings.shareDialogCopyButtonAriaLabelText,
                                copiedButton: localizedStrings.shareDialogCopiedButtonText,
                                copiedButtonAriaLabel: localizedStrings.shareDialogCopiedButtonAriaLabelText
                            };

                            newButtonItem.buttonData.dialogArticleCardProps = {
                                articleId: state.documentId,
                                headlineTitle: {
                                    tag: undefined,
                                    children: articleTitle
                                },
                                href: sharingShortUrl,
                                image: {
                                    alt: articleTitle,
                                    src: articleImageUrl
                                },
                                provider: {
                                    name: {
                                        title: providerName,
                                        children: providerName
                                    },
                                    image: {
                                        src: providerLogoUrl,
                                        alt: providerName
                                    }
                                }
                            };

                            newButtonItem.buttonData.attributes = {
                                "aria-label": localizedStrings.shareDialogButtonAriaLabelText,
                                "aria-hidden": false,
                                title: localizedStrings.shareDialogButtonAriaLabelText
                            };
                        } else {
                            newButtonItem = undefined;
                        }
                        break;
                    case ButtonType.LikeDislike:
                        newButtonItem.buttonData = {
                            attributes: {
                                "aria-label": localizedStrings.likeDislikeButtonAriaLabelText,
                                "aria-hidden": false,
                                title: localizedStrings.likeDislikeButtonAriaLabelText
                            },
                            status: state.status
                        };
                        break;
                    case ButtonType.MoreOptions: {
                        const menuItems: CardContextMenuItem[] = [];
                        if (state.status === Status.Like) {
                            OptionItemUndoLike.text = localizedStrings.moreOptionsUnlikeText;
                            OptionItemUndoLike.attr = {
                                "aria-label": localizedStrings.moreOptionsUnlikeAriaLabelText,
                                "aria-hidden": false,
                                title: localizedStrings.moreOptionsUnlikeAriaLabelText
                            };
                            menuItems.push(OptionItemUndoLike);

                            OptionItemDislike.text = localizedStrings.moreOptionsDislikeText;
                            OptionItemDislike.attr = {
                                "aria-label": localizedStrings.moreOptionsDislikeAriaLabelText,
                                "aria-hidden": false,
                                title: localizedStrings.moreOptionsDislikeAriaLabelText
                            };
                            menuItems.push(OptionItemDislike);
                        } else if (state.status === Status.DisLike) {
                            OptionItemUndoDislike.text = localizedStrings.moreOptionsUndislikeText;
                            OptionItemUndoDislike.attr = {
                                "aria-label": localizedStrings.moreOptionsUndislikeAriaLabelText,
                                "aria-hidden": false,
                                title: localizedStrings.moreOptionsUndislikeAriaLabelText
                            };
                            menuItems.push(OptionItemUndoDislike);

                            OptionItemLike.text = localizedStrings.moreOptionsLikeText;
                            OptionItemLike.attr = {
                                "aria-label": localizedStrings.moreOptionsLikeAriaLabelText,
                                "aria-hidden": false,
                                title: localizedStrings.moreOptionsLikeAriaLabelText
                            };
                            menuItems.push(OptionItemLike);
                        } else {
                            OptionItemDislike.text = localizedStrings.moreOptionsDislikeText;
                            OptionItemDislike.attr = {
                                "aria-label": localizedStrings.moreOptionsDislikeAriaLabelText,
                                "aria-hidden": false,
                                title: localizedStrings.moreOptionsDislikeAriaLabelText
                            };
                            menuItems.push(OptionItemDislike);
                        }
                        newButtonItem.buttonData = {
                            attributes: {
                                "aria-label": localizedStrings.moreOptionsButtonAriaLabelText,
                                "aria-hidden": false,
                                title: localizedStrings.moreOptionsButtonAriaLabelText
                            },
                            menuItems: menuItems,
                            status: state.status,
                            offsetPos: buttonItem.buttonData ? buttonItem.buttonData.offsetPos : undefined,
                            zIndex: buttonItem.buttonData ? buttonItem.buttonData.zIndex : undefined
                        };
                        break;
                    }
                    default:
                        break;
                }
                return newButtonItem;
            }).filter(value => value);
        }

        return {
            props: newProps,
            missingChildren: []
        };
    }
}